/**
 * Cancel logic (#678) with no React in it:
 *   - building the signed `cancel` action {type:'cancel', cancels:[{a, o}]} (asset id ONLY from hl/outcomes.ts),
 *   - sign (wallet, chain 1337) + POST /exchange via the same signAndPost used by the order ticket,
 *   - parsing the per-cancel statuses ("success" or {error}).
 *
 * Only outcome orders ("#<encoding>" coins) can be canceled here: spot / perp asset ids are not ours to compute.
 */
import { assetId, fromCoin } from '../hl/outcomes';
import { cancelAction, type CancelInput } from '../hl/signing';
import type { ExchangeResponse } from '../hl/exchange';
import { signAndPost, type SubmitDeps } from './order';
import { t } from '../i18n/i18n';

export type CancelActionT = ReturnType<typeof cancelAction>;

export interface CancelTarget {
  coin: string;
  oid: number;
}

/** True when `coin` is an outcome coin this app can cancel. */
export function isCancellableCoin(coin: string): boolean {
  return fromCoin(coin) !== null;
}

/** One `cancel` action for one or more outcome orders (one signature). Throws on a non-outcome coin / bad oid. */
export function buildCancelAction(targets: readonly CancelTarget[]): CancelActionT {
  if (targets.length === 0) throw new Error('nothing to cancel');
  const cancels: CancelInput[] = targets.map((t) => {
    const ref = fromCoin(t.coin);
    if (!ref) throw new Error(`cannot cancel non-outcome order ${t.coin}`);
    if (!Number.isSafeInteger(t.oid) || t.oid < 0) throw new Error(`invalid oid ${t.oid}`);
    return { a: assetId(ref.outcome, ref.side), o: t.oid };
  });
  return cancelAction(cancels);
}

export type CancelStatus = { kind: 'success' } | { kind: 'error'; message: string };

export type CancelResult =
  | { kind: 'ok'; statuses: CancelStatus[] }
  | { kind: 'err'; message: string }
  | { kind: 'rejected'; hint?: string };

/** Parse a POST /exchange response for a `cancel` action. Never throws. */
export function parseCancelResponse(resp: ExchangeResponse | unknown): CancelResult {
  if (!resp || typeof resp !== 'object') return { kind: 'err', message: `Unexpected response: ${JSON.stringify(resp)}` };
  const r = resp as { status?: unknown; response?: unknown };
  if (r.status !== 'ok') {
    const msg = typeof r.response === 'string' ? r.response : JSON.stringify(r.response ?? resp);
    return { kind: 'err', message: msg };
  }
  const data = (r.response as { data?: { statuses?: unknown } } | undefined)?.data;
  const raw = Array.isArray(data?.statuses) ? (data.statuses as unknown[]) : null;
  if (!raw) return { kind: 'err', message: `Unexpected response: ${JSON.stringify(resp)}` };
  const statuses = raw.map((s): CancelStatus => {
    if (s === 'success') return { kind: 'success' };
    if (s && typeof s === 'object' && typeof (s as { error?: unknown }).error === 'string') {
      return { kind: 'error', message: (s as { error: string }).error };
    }
    if (typeof s === 'string') return { kind: 'error', message: s };
    return { kind: 'error', message: `Unexpected status: ${JSON.stringify(s)}` };
  });
  return { kind: 'ok', statuses };
}

/** Sign + submit a `cancel` action (chain 1337 switch, wallet signature, POST /exchange) and parse the statuses. */
export async function signAndSubmitCancel(action: CancelActionT, deps: SubmitDeps): Promise<CancelResult> {
  const r = await signAndPost(action, deps);
  return r.kind === 'ok' ? parseCancelResponse(r.resp) : r;
}

/** Short user-facing text for a cancel error ("Order was never placed, already canceled, or filled. asset=..." → first sentence). */
export function cancelErrorText(message: string): string {
  const m = /^(Order was never placed, already canceled, or filled)\b/.exec(message);
  return m ? t('cancel.never_placed') : message;
}
